import { useCallback, useContext, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import emptyFunction from 'react-utils/emptyFunction';
import ContentButton from '../../../draft-content-transplant/ContentButton';
import { RTEToolbarButton } from '../../components/StyledTextToolbarButton';
import { TextToolbarIcon } from 'rich-text-lib/components/editor/TextToolbarIcon';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import KnowledgePopoverEmptyState from './draft-content-transplant-ka/KnowledgePopoverEmptyState';
import KnowledgePopoverErrorState from './draft-content-transplant-ka/KnowledgePopoverErrorState';
import transformArticlesData from './draft-content-transplant-ka/transformArticlesData';
import * as KnowledgeArticlesApi from './draft-content-transplant-ka/KnowledgeArticlesApi';
import contentOutputs from '../../editorCapabilities/ContentOutputTypes';
import hubHttpClient from 'hub-http/clients/apiClient';
import useDebouncedEditorStateContext from '../../contexts/useDebouncedEditorStateContext';
import GenericPopoverHeader from '../../components/GenericPopoverHeader';
import PortalIdParser from 'PortalIdParser';
import identity from 'transmute/identity';
import { convertToPlaintextState } from '../../utils/plaintextUtils';
import { insertLink } from '../../utils/documentUtils';
import KBArticlesTypeahead from './KBArticlesTypeahead';
import { getKnowledgeUrl } from '../../utils/manageLinks';
import InsertContentDecorator from '../../../draft-transplant/InsertContentDecoratorV2';
import { EditorConfigContext } from '../../contexts/EditorConfigContext';
import MultiKBs from './MultiKBs';
import { getGlobalVars } from '../../utils/globalVarUtil';
import { CapIcon } from 'foundations-assets/icons/components/CapIcon';

/** Configuration for the Knowledge Article insert capability */
import { jsx as _jsx } from "react/jsx-runtime";
const KnowledgeArticleButton = ({
  onClick
}) => /*#__PURE__*/_jsx(Tooltip, {
  title: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "hubspot-prosemirror.tooltips.insertKnowledgeArticle"
  }),
  children: /*#__PURE__*/_jsx(RTEToolbarButton, {
    "data-test-id": "knowledge-articles",
    onClick: onClick,
    selected: false,
    children: /*#__PURE__*/_jsx(TextToolbarIcon, {
      icon: CapIcon
    })
  })
});
export default function InsertKnowledgeArticle({
  useContentContainer,
  onViewInsertMenu = emptyFunction,
  onInsertComplete = emptyFunction,
  Header = GenericPopoverHeader,
  onOpenPopover = identity,
  PopoverEmptyState = KnowledgePopoverEmptyState,
  PopoverErrorState = KnowledgePopoverErrorState
}) {
  const {
    editorState,
    setEditorState
  } = useDebouncedEditorStateContext();
  const editorConfig = useContext(EditorConfigContext);
  const {
    apiClient = hubHttpClient,
    onInsertContent = identity,
    onSearchAllKBs,
    onSelectKB,
    outputType = contentOutputs.RENDERED,
    useMultipleKBs = false
  } = editorConfig.KNOWLEDGE_ARTICLE || {};
  const {
    usePlaintextMode,
    plaintextOptions
  } = useMemo(() => getGlobalVars(editorConfig), [editorConfig]);
  const insertArticle = useCallback(({
    htmlBody
  }) => {
    const tr = insertLink({
      htmlBody,
      editorState,
      to: editorState.selection.to,
      from: editorState.selection.from
    });
    setEditorState(usePlaintextMode ? convertToPlaintextState(editorState.apply(tr), plaintextOptions) : editorState.apply(tr));
    onInsertComplete();
  }, [editorState, setEditorState, usePlaintextMode, plaintextOptions, onInsertComplete]);
  const {
    apiFunctions,
    insertKnowledgeArticlesDecoratorConfig
  } = useMemo(() => {
    const api = KnowledgeArticlesApi.defaultApiWrapper(apiClient);
    return {
      apiFunctions: api,
      insertKnowledgeArticlesDecoratorConfig: {
        outputType,
        apiFunctions: api,
        mergeTagOptions: {
          includeCustomTokens: true
        }
      }
    };
  }, [apiClient, outputType]);
  const KnowledgeArticlesPopover = useMemo(() => {
    return InsertContentDecorator(ContentButton({
      onInsertContent,
      // @ts-expect-error ts-migrate FIXME
      portalId: PortalIdParser.get(),
      Button: KnowledgeArticleButton,
      PopoverErrorState,
      PopoverEmptyState,
      onOpenPopover,
      Header,
      searchPlaceHolder: 'draftPlugins.knowledgeArticlesPlugin.popover.search.placeholder',
      transformData: transformArticlesData,
      search: apiFunctions.searchArticles,
      popoverTestKey: 'knowledge-articles-popover',
      // @ts-expect-error portalId can be undefined in unit testing for example
      manageUrl: getKnowledgeUrl(PortalIdParser.get()),
      titleMessage: 'draftPlugins.knowledgeArticlesPlugin.popover.header.insertKnowledgeArticle'
    }), insertKnowledgeArticlesDecoratorConfig);
  }, [insertKnowledgeArticlesDecoratorConfig, onInsertContent, onOpenPopover, apiFunctions.searchArticles, Header, PopoverEmptyState, PopoverErrorState]);
  if (useMultipleKBs) {
    return /*#__PURE__*/_jsx(MultiKBs, {
      onClickBack: onViewInsertMenu,
      onSelectArticle: insertArticle,
      onSearchAllKBs: onSearchAllKBs,
      onSelectKB: onSelectKB,
      portalId: PortalIdParser.get()
    });
  }
  return useContentContainer ? /*#__PURE__*/_jsx(KBArticlesTypeahead, {
    apiFunctions: apiFunctions,
    onChange: insertArticle,
    onClickBack: onViewInsertMenu,
    portalId: PortalIdParser.get()
  }) : /*#__PURE__*/_jsx(KnowledgeArticlesPopover, {
    onChange: insertArticle,
    useProsemirror: true
  });
}